<template>
  <div class="step-upload-content">
    <!-- Header main -->
    <div class="header-main step-upload-content__header-main">
      <h1 class="global-h1 header-main__title">Upload Photos for your Social Posts</h1>

      <div class="header-main__subtitle">These photo groups will be the basis for your content calendar.</div>

      <HowContentCalendarWorksSpoiler class="header-main__how-does-it-work-spoiler" />

      <div v-if="!props.isLoading" style="gap: 20px; display: flex; flex-direction: column">
        <FutureContentNeeds
          v-if="hasFutureCalendarData"
          :nextCalendarGenSummary="nextCalendarGenSummary"
          :longTermCalendarGenSummary="longTermCalendarGenSummary"
          :customerId="props.customerId"
          :nextPostGenOnWeek="nextPostGenOnWeek"
          :hideActions="true"
          :hidePostGuidance="true"
        />
        <NoteWithIcon v-else color="green" style="max-width: 800px">
          <template #text>
            <h4 class="global-h4">Upload photos to get started</h4>
            <div class="global-p" style="margin-top: 8px">
              Your existing groups work great, no re-organizing required.
              <div style="margin-top: 3px; margin-left: 10px">- e.g., <span style="font-weight: 600">locations, events, seasons</span></div>
              <div style="margin-left: 10px">- photos only for now (max {{ MAX_ASSETS_PER_GROUP }} per group)</div>
            </div>
            <div class="global-p" style="margin-top: 8px"><b>Questions?</b> Contact us anytime! <CopyTextNew>support@shrpa.com</CopyTextNew></div>
          </template>
        </NoteWithIcon>
      </div>
    </div>
    <!-- / Header main -->

    <div v-if="props.isLoading" style="margin-bottom: 35px; display: flex; flex-direction: column; align-items: center; text-align: center">
      <LoaderNew style="width: 70px; height: 70px; margin-bottom: 10px" :borderWidth="3" />
      <div class="global-p">
        <h2>Loading...</h2>
      </div>
    </div>

    <template v-else>
      <AllPhotosSection
        :customerId="props.customerId"
        :onlyShowCustomerUploads="true"
        :isVideoUploadAllowed="false"
        :showTotalAssetCount="false"
        :currentOperationSource="hasFutureCalendarData ? PhotoGallerySource.CalendarAddingContent : PhotoGallerySource.CalendarManual"
        @dataLoaded="handleDataLoaded"
        @galleryGroupsUpdated="handleGalleryGroupsUpdated"
        @anyUploadingChanged="val => (isAnyUploadInProgress = val)"
      />

      <!-- Manage buttons floating panel -->
      <div class="manage-buttons-floating-panel step-upload-content__manage-buttons-floating-panel">
        <div class="manage-buttons-floating-panel__side-left"></div>

        <div class="manage-buttons-floating-panel__side-right">
          <!--<SrpButton class="manage-buttons-floating-panel__button" color="gray" fill="outlined" @click="emit('goBack')" :isDisabled="isLoading" iconInset="0 auto auto auto">
            <template #icon><IconEmbedded name="arrow-left_3-5" :size="17" /></template>
            Go back
          </SrpButton>-->

          <div v-if="getTotalPhotos() > 0 && !hasFutureCalendarData" class="manage-buttons-floating-panel__photo-count-info">
            <div class="photo-count-info__photos">{{ getTotalPhotos() }} photos</div>
            <div class="photo-count-info__groups">in {{ getGroupsWithPhotos() }} {{ getGroupsWithPhotos() === 1 ? "group" : "groups" }}</div>
          </div>

          <SrpButton
            class="manage-buttons-floating-panel__button"
            color="teal"
            :fill="getProceedButtonFill()"
            @click="handleProceedClick"
            :isDisabled="isLoading || !shouldShowProceedButton() || isProceeding || isAnyUploadInProgress"
            iconInset="0 auto auto auto"
          >
            <template #iconRight><IconEmbedded name="arrow-right_3-5" :size="17" /></template>
            {{ hasFutureCalendarData ? "Done" : "Proceed" }}
          </SrpButton>
        </div>
      </div>
      <!-- / Manage buttons floating panel -->
    </template>

    <!-- Minimum photos warning modal -->
    <SrpModal v-model:isVisible="isMinPhotosWarningModalVisible" :isClosable="true" :isCloseButtonVisible="true" size="medium">
      <template #content>
        <div class="min-photos-warning-modal">
          <div class="standard-page-header standard-page-header--no-bottom-line min-photos-warning-modal__header">
            <h2 class="global-h2 standard-page-header__title">Please add more photos</h2>
          </div>

          <div class="global-p" style="margin-top: 10px">
            To proceed, you need:
            <ul style="margin-top: 8px; margin-bottom: 8px">
              <li>At least <strong>3 groups</strong></li>
              <li>With at least <strong>10 photos in each</strong> group</li>
            </ul>
            This helps us create a diverse and engaging content calendar for you.
          </div>
        </div>
      </template>

      <template #footer="{ closeModal }">
        <SrpButton @click="closeModal">Add More Photos</SrpButton>
        <!--<SrpButton color="red" fill="outlined" @click="handleProceedAnywayFromWarning">Proceed Anyway</SrpButton>-->
      </template>
    </SrpModal>

    <!-- Leave page warning modal -->
    <SrpModal v-model:isVisible="isLeavePageWarningVisible" :isClosable="true" :isCloseButtonVisible="true" size="medium">
      <template #content>
        <div class="leave-page-warning-modal">
          <div class="standard-page-header standard-page-header--no-bottom-line leave-page-warning-modal__header">
            <h2 class="global-h2 standard-page-header__title">Uploading in progress</h2>
          </div>

          <div class="global-p" style="margin-top: 10px">We're still uploading your files. Leaving this page now may stop the upload.</div>
        </div>
      </template>

      <template #footer="{ closeModal }">
        <SrpButton @click="closeModal">Finish uploading</SrpButton>
        <SrpButton color="gray" fill="outlined" @click="handleLeavePage">Leave page</SrpButton>
      </template>
    </SrpModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject } from "vue";
import { onBeforeRouteLeave, useRouter } from "vue-router";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";

const globalRemoteLogger = inject("globalRemoteLogger") as any;
const router = useRouter();

// Components
import NoteWithIcon from "@components/NoteWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import AllPhotosSection from "@components/AllPhotosSection/index.vue";
import LoaderNew from "@components/Loader/LoaderNew.vue";
import HowContentCalendarWorksSpoiler from "@views/Community/ContentCalendar/HowContentCalendarWorksSpoiler.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import FutureContentNeeds from "../FutureContentNeeds.vue";
import CopyTextNew from "@components/CopyTextNew.vue";

// Types
import { PhotoGallerySource, PhotoGalleryCustomerData, MAX_ASSETS_PER_GROUP } from "@contracts/photosPageData";
import { CalendarGenResultSummaryData } from "@contracts/contentCalendar";

const props = withDefaults(
  defineProps<{
    customerId: string;
    galleryGroups: Array<PhotoGalleryCustomerData>;
    isLoading: boolean;
  }>(),
  {}
);

const emit = defineEmits<{
  (e: "proceed"): void;
  (e: "update:galleryGroups", updatedGalleryGroups: Array<PhotoGalleryCustomerData>): void;
}>();

// Future calendar data =======================================================
const nextCalendarGenSummary = ref<CalendarGenResultSummaryData | null>(null);
const longTermCalendarGenSummary = ref<CalendarGenResultSummaryData | null>(null);
const nextPostGenOnWeek = ref<string | null>(null);

const hasFutureCalendarData = computed<boolean>(() => {
  return Boolean(nextCalendarGenSummary.value || longTermCalendarGenSummary.value);
});

// Fetch calendar data on mount
onMounted(async () => {
  const calendarData = await ContentCalendarRepo.getNextFutureCalendarSummary(props.customerId);
  nextCalendarGenSummary.value = calendarData.nextCalendarGenSummary;
  longTermCalendarGenSummary.value = calendarData.longTermCalendarGenSummary;
  nextPostGenOnWeek.value = null; // Not provided by this endpoint
});

// Minimum photos warning =====================================================
const isMinPhotosWarningModalVisible = ref<boolean>(false);
const MIN_PHOTOS_PER_GROUP = 10;
const MIN_GROUPS_WITH_MIN_PHOTOS = 3;

// Track if user has taken any action with gallery groups
const isActionTaken = ref<boolean>(false);

// Computed values for button state ===========================================
function getTotalPhotos(): number {
  return props.galleryGroups.reduce((sum, g) => sum + g.photoIds.length, 0);
}

function getGroupsWithPhotos(): number {
  return props.galleryGroups.filter(g => g.photoIds.length > 0).length;
}

function getProceedButtonFill(): "outlined" | "solid" {
  const groupsWithMinPhotos = props.galleryGroups.filter(g => g.photoIds.length >= MIN_PHOTOS_PER_GROUP).length;
  return groupsWithMinPhotos >= MIN_GROUPS_WITH_MIN_PHOTOS ? "solid" : "outlined";
}

function shouldShowProceedButton(): boolean {
  return getTotalPhotos() > 0;
}

const isProceeding = ref<boolean>(false);
const isAnyUploadInProgress = ref<boolean>(false);

// Navigation warning when uploads are in progress ===========================
const isLeavePageWarningVisible = ref<boolean>(false);
const pendingNavigation = ref<any>(null);

async function handleProceedClick() {
  // Check if user has enough photos - need at least X groups with at least Y photos in each
  const groupsWithMinPhotos = props.galleryGroups.filter(g => g.photoIds.length >= MIN_PHOTOS_PER_GROUP).length;

  if (groupsWithMinPhotos < MIN_GROUPS_WITH_MIN_PHOTOS) {
    isMinPhotosWarningModalVisible.value = true;
    return;
  }

  isProceeding.value = true;

  // Skip finishedUploading call if nothing changed.
  // This prevents unnecessary reprocessing for live calendars since content updates are cross-calendar.
  var skipApiCall = !isActionTaken.value && hasFutureCalendarData.value;
  if (skipApiCall) globalRemoteLogger.info(`ContentCalendar: Skipped finishedUploading, no changes. customerId=${props.customerId}`, true);
  else await ContentCalendarRepo.finishedUploading(props.customerId);

  emit("proceed");
}

// Handle data loaded from AllPhotosSection ==================================
function handleDataLoaded(data: any) {
  if (data.customerGalleryPhotoGroups) {
    emit("update:galleryGroups", data.customerGalleryPhotoGroups);
  }
}

// Handle gallery groups updated from AllPhotosSection =======================
function handleGalleryGroupsUpdated(galleryGroups: Array<PhotoGalleryCustomerData>) {
  isActionTaken.value = true;
  emit("update:galleryGroups", galleryGroups);
}

// Navigation guard to warn if uploads are in progress =======================
onBeforeRouteLeave(to => {
  if (isAnyUploadInProgress.value) {
    // Block navigation and show warning modal
    pendingNavigation.value = to;
    isLeavePageWarningVisible.value = true;
    return false;
  }
  // Allow navigation
  return true;
});

function handleLeavePage() {
  isLeavePageWarningVisible.value = false;
  if (pendingNavigation.value) {
    // Temporarily set to false to allow navigation through the guard
    const wasUploading = isAnyUploadInProgress.value;
    isAnyUploadInProgress.value = false;
    router.push(pendingNavigation.value).catch(() => {
      // If navigation fails for any reason, restore the state
      isAnyUploadInProgress.value = wasUploading;
    });
  }
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/or-divider.scss";
@import "./headers.scss";

// Min photos warning modal ===================================================
.min-photos-warning-modal {
  &__header {
    padding-bottom: 0 !important;
  }
}

// Manage buttons floating panel ==============================================
.manage-buttons-floating-panel {
  gap: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 0 20px 20px rgba(255, 255, 255, 1);
  background: rgba(255, 255, 255, 1);

  &__side-left {
    gap: 12px;
    display: flex;
    align-items: center;
  }

  &__side-right {
    gap: 12px;
    display: flex;
    align-items: center;
  }

  &__photo-count-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-size: 14px;
    line-height: 1.3;
    color: #333;
    font-weight: 600;
  }
}

// Step upload content ========================================================
.step-upload-content {
  &__header-main {
    margin-bottom: 35px;
  }

  &__add-section-button {
    margin-bottom: 30px;
  }

  &__divider {
    margin-bottom: 30px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__manage-buttons-floating-panel {
    margin-top: 25px;
    margin-bottom: 35px;
    position: sticky;
    inset: auto auto 15px 0;
  }
}
</style>
